import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MessageSquare, Send } from 'lucide-react';
import emailjs from '@emailjs/browser';
import axios from 'axios';
import { CONTACT } from '../../mock';
import { useToast } from '../../hooks/use-toast';

export default function HammerContact() {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    smsConsent: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const phoneLines = useMemo(
    () => CONTACT.phones.map((p) => `${p.number} — ${p.name}`),
    []
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast({ title: 'Missing details', description: 'Please add your name and phone.' });
      return;
    }
    if (!form.smsConsent) {
      toast({
        title: 'One more step',
        description: 'Please check the box to consent to SMS messages so we can respond to your inquiry.',
      });
      return;
    }
    setSubmitting(true);
    // Save the lead to the backend DB in parallel with the EmailJS send.
    // Errors are swallowed so a DB hiccup can't break the email flow.
    axios
      .post(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        message: form.message || null,
        source: 'hammer',
        sms_consent: form.smsConsent,
        last_estimate: (() => {
          try {
            const raw = window.localStorage.getItem('last_estimate');
            return raw ? JSON.parse(raw) : null;
          } catch (_) { return null; }
        })(),
      })
      .catch(() => {});
    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        // Falls back to the barge template if the hammer-specific one isn't
        // provisioned yet — keeps submissions working during the switchover.
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID_HAMMER ||
          process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          name: form.name,
          phone: form.phone,
          email: form.email || 'not-provided@absolutehammers.com',
          // Prefix the message so the shared owner inbox knows this is a
          // hammer-source lead. Once a backend endpoint exists we'll split
          // these into a dedicated template + a proper `source` field.
          message: `[HAMMER LEAD] ${form.message || '(no additional details)'}`,
          sms_consent: form.smsConsent
            ? 'YES — checked SMS consent on absolutehammers.com contact form'
            : 'NO',
          time: new Date().toLocaleString('en-US', {
            timeZone: 'America/New_York',
            dateStyle: 'medium',
            timeStyle: 'short',
          }),
        },
        { publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY }
      );
      toast({
        title: 'Message sent',
        description: "We'll reach out within one business day.",
      });
      setForm({ name: '', phone: '', email: '', message: '', smsConsent: false });
    } catch (err) {
      toast({
        title: "Couldn't send message",
        description:
          err && (err.text || err.message)
            ? `${err.text || err.message}. If this persists, call ${CONTACT.salesPhone.number}.`
            : `Please try again or call ${CONTACT.salesPhone.number} directly.`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="hammer-contact"
      data-testid="hammer-contact-section"
      className="relative py-16 sm:py-20 lg:py-28 bg-black border-t border-white/5"
    >
      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-3 sm:mb-4">
            <span className="h-px w-8 sm:w-10 bg-cyan-400" />
            <span className="font-tech text-cyan-300 text-[10px] sm:text-[11px] uppercase tracking-[0.28em]">
              Order · Questions · Freight
            </span>
          </div>
          <h2 className="font-display text-white text-3xl sm:text-4xl md:text-5xl font-bold uppercase leading-[1.02] tracking-tight">
            Get your <span className="text-cyan-300">Absolute Hammer.</span>
          </h2>
          <p className="mt-5 text-zinc-400 text-sm sm:text-base md:text-lg leading-relaxed">
            Tell us your ship-to and we'll come back with a firm freight quote,
            estimated delivery date, and payment instructions. We answer every
            inquiry within one business day.
          </p>
        </div>

        <div className="mt-10 sm:mt-14 grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10">
          {/* Contact rail */}
          <div className="space-y-6">
            <div className="panel rounded-xl p-5 sm:p-6">
              <div className="text-[11px] uppercase tracking-[0.24em] text-zinc-500 mb-3">
                Direct Lines
              </div>
              <ul className="space-y-2 text-zinc-200 text-sm sm:text-base">
                {phoneLines.map((l) => (
                  <li key={l} className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-cyan-300" />
                    {l}
                  </li>
                ))}
              </ul>
            </div>

            <div className="panel rounded-xl p-5 sm:p-6">
              <div className="text-[11px] uppercase tracking-[0.24em] text-zinc-500 mb-3">
                Email
              </div>
              <a
                href={`mailto:${CONTACT.email}`}
                className="flex items-center gap-2 text-zinc-200 hover:text-cyan-300 transition-colors break-all"
              >
                <Mail className="w-4 h-4 text-cyan-300 shrink-0" /> {CONTACT.email}
              </a>
            </div>

            <div className="flex flex-wrap gap-3">
              <a
                href={`tel:${CONTACT.salesPhone.tel}`}
                data-testid="hammer-contact-call-btn"
                className="btn-teal px-5 py-2.5 rounded-md text-sm inline-flex items-center gap-2"
              >
                <Phone className="w-4 h-4" /> Call {CONTACT.salesPhone.number}
              </a>
              <a
                href={`sms:${CONTACT.salesPhone.tel}`}
                data-testid="hammer-contact-sms-btn"
                className="btn-ghost px-5 py-2.5 rounded-md text-sm inline-flex items-center gap-2"
              >
                <MessageSquare className="w-4 h-4" /> Text us
              </a>
            </div>
          </div>

          {/* Form */}
          <form
            id="hammer-quote"
            onSubmit={submit}
            className="panel rounded-xl p-6 sm:p-7 lg:p-9 scroll-mt-24"
            data-testid="hammer-quote-form"
          >
            <h3 className="font-display text-white text-xl sm:text-2xl font-semibold tracking-wide">
              Request a Hammer
            </h3>
            <p className="text-zinc-500 text-sm mt-1">
              $10,500* · we respond within one business day.
            </p>

            <div className="mt-7 space-y-5">
              <Field label="Your Name">
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-black/60 border border-white/10 focus:border-cyan-400/60 outline-none rounded-md px-4 py-3 text-white"
                  placeholder="John Doe"
                  data-testid="hammer-form-name"
                />
              </Field>
              <Field label="Phone">
                <input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full bg-black/60 border border-white/10 focus:border-cyan-400/60 outline-none rounded-md px-4 py-3 text-white"
                  placeholder="(321) 000-0000"
                  data-testid="hammer-form-phone"
                />
              </Field>
              <Field label="Email (optional)">
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-black/60 border border-white/10 focus:border-cyan-400/60 outline-none rounded-md px-4 py-3 text-white"
                  placeholder="you@example.com"
                  data-testid="hammer-form-email"
                />
              </Field>
              <Field label="Message">
                <textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={4}
                  className="w-full bg-black/60 border border-white/10 focus:border-cyan-400/60 outline-none rounded-md px-4 py-3 text-white resize-none"
                  placeholder="How many hammers, ship-to city / state, and any questions."
                  data-testid="hammer-form-message"
                />
              </Field>

              {/* Honeypot */}
              <input
                type="checkbox"
                name="botcheck"
                tabIndex={-1}
                autoComplete="off"
                style={{ display: 'none' }}
              />

              <label
                className="flex gap-3 items-start cursor-pointer group"
                data-testid="hammer-sms-consent-block"
              >
                <input
                  type="checkbox"
                  checked={form.smsConsent}
                  onChange={(e) => setForm({ ...form, smsConsent: e.target.checked })}
                  className="mt-1 w-4 h-4 shrink-0 accent-cyan-400 cursor-pointer"
                  data-testid="hammer-sms-consent-checkbox"
                />
                <span className="text-[12px] leading-relaxed text-zinc-400 group-hover:text-zinc-300 transition-colors">
                  By checking this box and submitting this form, I consent to receive calls and SMS/MMS text messages from{' '}
                  <span className="text-zinc-200">Absolute Marine Equipment</span> at the phone number provided, related to my inquiry, quote, order, and delivery. Message frequency varies. Message &amp; data rates may apply. Reply{' '}
                  <span className="text-zinc-200">STOP</span> to opt out or{' '}
                  <span className="text-zinc-200">HELP</span> for help. Consent is not a condition of purchase. See our{' '}
                  <Link to="/privacy" className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2">
                    Privacy Policy
                  </Link>{' '}
                  and{' '}
                  <Link to="/sms-terms" className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2">
                    SMS Terms
                  </Link>
                  .
                </span>
              </label>

              <button
                type="submit"
                disabled={submitting}
                data-testid="hammer-form-submit"
                className="btn-teal w-full px-5 py-3 rounded-md inline-flex items-center justify-center gap-2 text-sm uppercase tracking-[0.18em] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" /> {submitting ? 'Sending…' : 'Send Inquiry'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-2">
        {label}
      </div>
      {children}
    </label>
  );
}
